/**
 * Licensed travel photography. Every photo here is free under the Unsplash License
 * (https://unsplash.com/license); credits are listed in docs/IMAGE_CREDITS.md.
 * Photos are decorative: they never carry information the user needs, and every slot
 * degrades to a calm gradient if the image is missing or fails to load.
 */
import { useState, type ReactNode } from 'react';
import { cx } from '../components/ui';
import { PHOTO_SOURCES, type PhotoSlot } from './photoSources';

export type { PhotoSlot };

/** Self-hosted files win; otherwise a sized Unsplash CDN URL. */
export function photoSrc(slot: PhotoSlot, width = 1200): string | null {
  const p = PHOTO_SOURCES[slot];
  if (!p) return null;
  if (p.local) return p.local;
  return `${p.image}?auto=format&fit=crop&w=${width}&q=70`;
}

const PLACE_SLOTS: [RegExp, PhotoSlot][] = [
  [/bruges|brugge/i, 'bruges'], [/amsterdam/i, 'amsterdam'], [/brussel|bruxelles/i, 'brussels'],
  [/london/i, 'london'], [/paris/i, 'paris'], [/lisbon|lisboa/i, 'lisbon'],
  [/beach|island|maldives|bali|caribbean/i, 'beach'], [/alps|mountain|ski|lake/i, 'mountains'],
  [/coast|amalfi|cinque|riviera/i, 'coast'],
];

/** Pick a photo from place names, most relevant first (e.g. current city, then the rest). */
export function slotForPlaces(...texts: (string | null | undefined)[]): PhotoSlot {
  for (const text of texts) {
    if (!text) continue;
    for (const [re, slot] of PLACE_SLOTS) if (re.test(text) && PHOTO_SOURCES[slot]) return slot;
  }
  return PHOTO_SOURCES.map ? 'map' : 'coast';
}

const FALLBACK = 'bg-[radial-gradient(120%_120%_at_0%_0%,#18b7b0_0%,#0a998a_50%,#087f70_100%)]';

export function Photo({ slot, width = 1200, className, children, overlay = 'none', priority }: {
  slot: PhotoSlot; width?: number; className?: string; children?: ReactNode;
  overlay?: 'none' | 'bottom' | 'left' | 'full'; priority?: boolean;
}) {
  const src = photoSrc(slot, width);
  const [failed, setFailed] = useState(false);
  const p = PHOTO_SOURCES[slot];
  return (
    <div className={cx('relative flex flex-col overflow-hidden', FALLBACK, className)}>
      {src && !failed && (
        <img src={src} alt="" loading={priority ? 'eager' : 'lazy'} decoding="async" onError={() => setFailed(true)}
          className="absolute inset-0 size-full object-cover" draggable={false} title={p ? `Photo: ${p.photographer} / Unsplash` : undefined} />
      )}
      {overlay === 'bottom' && <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />}
      {overlay === 'left' && <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />}
      {overlay === 'full' && <div className="absolute inset-0 bg-black/35" />}
      {children && <div className="relative flex flex-1 flex-col">{children}</div>}
    </div>
  );
}
